import { CLU_MOVES, type CluMove } from '@/components/ui/clu-moves';
import { useCallback, useEffect, useRef, useState } from 'react';

export const CLU_SALUTE_FIRST_WAVE_MS = 1000;
export const CLU_SALUTE_REPEAT_MS = 30_000;
export const AGENT_WAVE_CHANCE = 0.1;
export const CLU_SALUTE_OPEN_DELAY_MS = 600;

// Shared across mounts, so a page change doesn't restart the rhythm.
let lastNudgeAt = 0;

export const resetCluSaluteClockForTests = () => {
  lastNudgeAt = 0;
};

const randomMove = (): CluMove =>
  CLU_MOVES[Math.floor(Math.random() * CLU_MOVES.length)];

/**
 * Clu's greeting. `wave()` plays a salute and `greet()` one of his moves at
 * random; `nudging` also greets on arrival and every `CLU_SALUTE_REPEAT_MS`.
 * `waveCount` counts every move.
 */
export function useCluSalute(nudging = false): {
  waveCount: number;
  move: CluMove;
  wave: () => void;
  greet: () => void;
} {
  const [cue, setCue] = useState<{ count: number; move: CluMove }>({
    count: 0,
    move: 'salute',
  });
  const play = useCallback(
    (move: CluMove) => setCue(({ count }) => ({ count: count + 1, move })),
    []
  );
  const wave = useCallback(() => play('salute'), [play]);
  const greet = useCallback(() => play(randomMove()), [play]);

  useEffect(() => {
    if (!nudging) return;
    let timer: number;
    const nudge = () => {
      if (document.visibilityState === 'visible') {
        lastNudgeAt = Date.now();
        greet();
      }
      timer = window.setTimeout(nudge, CLU_SALUTE_REPEAT_MS);
    };
    const firstIn =
      lastNudgeAt === 0
        ? CLU_SALUTE_FIRST_WAVE_MS
        : Math.max(
            CLU_SALUTE_FIRST_WAVE_MS,
            lastNudgeAt + CLU_SALUTE_REPEAT_MS - Date.now()
          );
    timer = window.setTimeout(nudge, firstIn);
    return () => window.clearTimeout(timer);
  }, [nudging, greet]);

  return { waveCount: cue.count, move: cue.move, wave, greet };
}

/** A wave with `chance` odds each time `openKey` opens, rolled once per key. */
export function useCluSaluteOnOpen(chance: number, openKey: string) {
  const salute = useCluSalute();
  const { wave } = salute;
  const roll = useRef<{ key: string; lucky: boolean } | null>(null);

  useEffect(() => {
    if (chance <= 0) return;
    if (roll.current?.key !== openKey) {
      roll.current = { key: openKey, lucky: Math.random() < chance };
    }
    if (!roll.current.lucky) return;
    const timer = window.setTimeout(wave, CLU_SALUTE_OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [chance, openKey, wave]);

  return salute;
}
